import { makeStyles } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Period } from 'types/Period';

export interface YearMonthPeriodProps {
  onChange: (period: Period) => any;
  minStartDate: Date;
  maxEndDate: Date;
  defaultValues: Period | null;
  useFullDate?: boolean;
  startDateError?: string | null;
  endDateError?: string | null;
}

const useStyles = makeStyles({
  container: {
    width: '100%',
    display: 'flex',
    flexFlow: 'row',
    alignItems: 'center',
  },
  datePicker: {
    marginTop: 8,
    width: '50%',
    margin: '1vw',
  },
  flex: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
  },
});

const YearMonthPeriod: React.FC<YearMonthPeriodProps> = ({
  onChange,
  defaultValues,
  minStartDate,
  maxEndDate,
  useFullDate,
  startDateError,
  endDateError,
}) => {
  const [startDate, setStartDate] = useState<Date | null>(defaultValues?.startDate ?? null);
  const [endDate, setEndDate] = useState<Date | null>(defaultValues?.endDate ?? null);
  const minEndDate =
    startDate ??
    (useFullDate ? minStartDate : new Date(Date.UTC(minStartDate.getFullYear(), minStartDate.getMonth(), 1)));
  const maxStartDate =
    endDate ?? (useFullDate ? maxEndDate : new Date(Date.UTC(maxEndDate.getFullYear(), maxEndDate.getMonth(), 1)));
  const classes = useStyles();
  const { t } = useTranslation();
  const minStartDateFirstDay = new Date(Date.UTC(minStartDate.getFullYear(), minStartDate.getMonth(), 1));
  const maxEndDateFirstDay = new Date(Date.UTC(maxEndDate.getFullYear(), maxEndDate.getMonth(), 1));

  useEffect(() => {
    if (startDate) {
      const newMinEndDate = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), 1));

      if (endDate && endDate < newMinEndDate) {
        setEndDate(newMinEndDate);
      }
    }
    if (endDate) {
      const newMaxStartDate = useFullDate ? endDate : new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), 1));

      if (startDate && startDate > newMaxStartDate) {
        setStartDate(newMaxStartDate);
      }
    }
  }, [startDate, endDate, useFullDate]);

  return (
    <div className={classes.container}>
      <div className={classes.flex}>
        <DatePicker
          className={classes.datePicker}
          views={useFullDate ? ['date'] : ['year', 'month']}
          format={useFullDate ? 'dd/MM/yyyy' : undefined}
          label={t('startDate')}
          clearLabel={t('delete')}
          error={startDateError ? true : false}
          helperText={startDateError}
          disableToolbar
          clearable
          cancelLabel={t('cancel')}
          minDate={useFullDate ? minStartDate : minStartDateFirstDay}
          initialFocusedDate={useFullDate ? minStartDate : minStartDateFirstDay}
          maxDate={maxStartDate}
          value={startDate}
          onChange={newStartDate => {
            onChange({
              startDate: newStartDate,
              endDate: endDate,
            });
            setStartDate(newStartDate);
          }}
        />
      </div>

      <div className={classes.flex}>
        <DatePicker
          className={classes.datePicker}
          views={useFullDate ? ['date'] : ['year', 'month']}
          format={useFullDate ? 'dd/MM/yyyy' : undefined}
          label={t('endDate')}
          clearLabel={t('delete')}
          error={endDateError ? true : false}
          helperText={endDateError}
          disableToolbar
          clearable
          cancelLabel={t('cancel')}
          minDate={minEndDate}
          maxDate={useFullDate ? maxEndDate : maxEndDateFirstDay}
          value={endDate}
          onChange={newEndDate => {
            onChange({
              startDate: startDate,
              endDate: newEndDate,
            });
            setEndDate(newEndDate);
          }}
        />
      </div>
    </div>
  );
};

export default YearMonthPeriod;
